import type { GroundReport } from '../depth/ground'
import type { Hud } from '../ui/hud'
import type { BodiesSnapshot } from './bodies'
import type { FloorMode } from './config'

/**
 * Every write to the stats panel, in one place.
 *
 * The panel is deliberately short. The research build this came from had forty
 * metric rows; all but these were instrumentation for questions that are now
 * answered and written down in docs/MEASUREMENTS.md. What is left is what a
 * visitor can act on while holding the phone.
 */

export interface FrameStats {
  /** Buffer size and format, e.g. `160x90 luminance-alpha`. */
  readonly depth: string
  readonly emptyCells: number
  readonly cells: number
  readonly floorMode: FloorMode
  readonly floor: GroundReport | null
  /** Hit-test has stopped returning results, so the plane is coasting. */
  readonly floorStale: boolean
  readonly bodies: BodiesSnapshot
  readonly stillSeconds: number
  readonly moving: boolean
}

/** Written once per FPS window by the frame loop. */
export function writeFps(hud: Hud, fps: number): void {
  hud.metric('fps', fps.toFixed(0), fps < 25 ? 'bad' : fps < 45 ? 'warn' : 'ok')
}

/**
 * Written once, at session start. `raw` has never been granted by any browser
 * tested; `—` means the browser has no `depthType` attribute at all, which is a
 * different answer from having one and choosing `smooth`.
 */
export function writeDepthType(hud: Hud, type: string): void {
  hud.metric('depth type', type, type === 'raw' ? 'ok' : 'warn')
}

/** Replaces the depth row while no depth frame can be read. */
export function writeDepthProblem(hud: Hud, message: string, holding: boolean): void {
  hud.metric('depth', holding ? 'holding' : message, holding ? 'warn' : 'bad')
}

export function writeFrameStats(hud: Hud, stats: FrameStats): void {
  hud.metric('depth', stats.depth)

  const holes = stats.cells > 0 ? (stats.emptyCells / stats.cells) * 100 : 0
  hud.metric('holes', `${holes.toFixed(1)}%`, holes > 25 ? 'warn' : 'ok')

  writeFloor(hud, stats)
  writeBodies(hud, stats.bodies)

  hud.metric(
    'still',
    `${stats.stillSeconds.toFixed(1)} s`,
    stats.moving ? 'ok' : stats.stillSeconds > 20 ? 'warn' : 'ok'
  )
}

function writeFloor(hud: Hud, stats: FrameStats): void {
  if (stats.floorMode === 'raw') {
    hud.metric('floor', 'raw — no plane')
    hud.metric('proud', '—')
    return
  }

  const plane = stats.floor?.plane
  if (!stats.floor || !plane) {
    hud.metric('floor', stats.floorMode === 'hit' ? 'waiting for hit-test' : 'no fit', 'warn')
    hud.metric('proud', '—')
    return
  }

  hud.metric(
    'floor',
    stats.floorMode === 'hit'
      ? `${(plane.height * 1000).toFixed(0)} mm${stats.floorStale ? ' · stale' : ''}`
      : `${(plane.rms * 1000).toFixed(1)} mm rms`,
    stats.floorStale ? 'warn' : 'ok'
  )

  // Cells the floor did not account for, i.e. real relief standing on it. This
  // is the "how much is being detected" number the skin shows in colour.
  const proud = stats.floor.valid - stats.floor.locked
  const share = stats.floor.valid > 0 ? (proud / stats.floor.valid) * 100 : 0
  hud.metric('proud', `${proud} · ${share.toFixed(1)}%`)
}

function writeBodies(hud: Hud, bodies: BodiesSnapshot): void {
  if (bodies.count === 0) {
    hud.metric('lost', '—')
    return
  }

  const lost = bodies.lostShare * 100
  hud.metric('lost', `${lost.toFixed(0)}%`, lost > 20 ? 'bad' : lost > 5 ? 'warn' : 'ok')
}
